import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../i18n/localeUtils'
import { Link } from 'react-router'
import { Box, Tooltip, Typography, useMediaQuery, useTheme, Stack } from '@mui/material'
import {
    DataGridPro,
    GridCallbackDetails,
    gridClasses,
    GridColDef,
    GridColumnOrderChangeParams,
    GridFilterModel,
    GridRenderCellParams,
    GridSlots,
    GridSortModel,
    GridTreeNodeWithRender,
    useGridApiRef,
} from '@mui/x-data-grid-pro'
import { getMuiDataGridLocaleText } from '../../i18n/localeUtils'
import * as datefns from 'date-fns'
import { ExtractAtomValue, useAtom } from 'jotai'
import {
    issueFiltersAtom,
    issueGridCurrentPageAtom,
    issueSearchTextfieldAtom,
    issueTableDataAtom,
    priceChangeSettings,
} from '../../atoms/issueAtom'
import {
    formatBigMoney,
    formatDateYYYYMMDD,
    formatMoney,
    formatPercentage,
} from '../../packages/utils/numberFormatters'
import { IssueStatusDescription } from './IssueStatus'
import { IssueTypeText } from './IssueType'
import OutcomeChart from './OutcomeChart'
import IssueTypeLabels from './IssueTypeLabels'
import { CommitChart } from './CommitChart'
import { isPreviousIssue } from '../utils/categorizeIssues'
import {
    calculateDilution,
    calculateDiscount,
    calculatePriceChange,
    getIssueSubscribedSize,
} from '../../pages/issues/utils'
import { ColoredPercentage } from './utils/ColoredPercentage'
import { PopoverSelector } from './PopoverSelector'
import { TableSettingHeader, TableSettingHeaderProps } from './TableSettingHeader'
import { CustomDataGridToolbar, CustomDataGridToolbarProps } from '../custom-grid-components/CustomDataGridToolbar'
import { FullHeightBox } from '../UIComponents'
import { createNoteIndicatorColumn, noteIndicatorGridSx } from './NoteRowIndicator'
import { useEntityNoteIds } from '../../hooks/useEntityNoteIds'
import { EnrichedIssueEntity, IssueEntity } from 'types/IssueEntity'
import { translateMillistreamSector } from '../../utils/millistreamSectors'
import { WarrantName } from './WarrantName'
import { getFeeDescription } from '../utils/getFeeDescription'
import { CustomColumnsPanel, CustomColumnsPanelProps } from '../custom-grid-components/CustomColumnsPanel'
import { IssueGridCols, WarrantListItem } from '@finance-lab/utils/types'

const ISSUE_TYPES_WITH_COMMIT_CHART = ['Rights issue', 'IPO']
const ISSUE_TYPES_WITH_OUTCOME_CHART = ['Rights issue', 'IPO']

const issuesCols = (
    tableSettings: ExtractAtomValue<typeof issueTableDataAtom>,
    onOpenTableSetting: TableSettingHeaderProps['onClick'],
    locale: string,
    t: (v: string) => string,
    isCompact: boolean,
    detailsPath: (v: IssueEntity) => string,
    entityIdsWithNotes: Set<string> | null,
    reloadEntityNoteIds: () => void,
) => {
    const LinkWrapper = ({ children, to }: { children?: ReactNode | null; to: string }) => (
        <Link
            to={to}
            style={{
                display: 'flex',
                width: '100%',
                height: '100%',
                textDecoration: 'none',
                color: '#000',
                justifyContent: 'inherit',
            }}
        >
            {children}
        </Link>
    )

    const defaultRenderCell = (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        params: GridRenderCellParams<EnrichedIssueEntity, any, any, GridTreeNodeWithRender>,
    ) => (
        <LinkWrapper to={detailsPath(params.row)}>
            <FullHeightBox>{params.formattedValue}</FullHeightBox>
        </LinkWrapper>
    )

    const cols: Array<GridColDef<EnrichedIssueEntity> & { field: IssueGridCols }> = [
        {
            field: 'issueLabel',
            headerName: t('entities.tableHeaders.issue'),
            headerAlign: 'left',
            minWidth: isCompact ? undefined : 400,
            flex: isCompact ? 2 : 1,
            align: 'left',
            valueGetter: (_, row) => row.shareName,
            renderCell: (params) => {
                return (
                    <LinkWrapper to={detailsPath(params.row)}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            sx={{
                                alignItems: { xs: 'start', md: 'center' },
                                paddingY: { xs: 2, md: 0 },
                            }}
                        >
                            <Stack sx={{ justifyContent: 'center', minWidth: '90px' }}>
                                <IssueTypeLabels
                                    hasWarrants={!!params.row.hasWarrants}
                                    issueType={params.row.type}
                                    isRto={!!params.row.rto}
                                    isSpac={!!params.row.spac}
                                    sharesInUnit={params.row.sharesInUnit}
                                    unitExtraContent={params.row.unitExtraContent}
                                />
                            </Stack>
                            <Stack>{params.row.shareName}</Stack>
                        </Stack>
                    </LinkWrapper>
                )
            },
        },
        {
            field: 'marketplace',
            headerName: t('entities.tableHeaders.marketList'),
            renderCell: defaultRenderCell,
            valueGetter: (_val, issue) => issue.marketplaceName,
        },
        {
            field: 'preMoney',
            headerName: t('entities.tableHeaders.preMoney'),
            renderCell: defaultRenderCell,
            valueFormatter: (val, issue) => (val ? formatBigMoney(val, { currencySymbol: issue.currency }) : '-'),
        },
        {
            field: 'subscriptionPrice',
            headerName: t('entities.tableHeaders.subscriptionPrice'),
            renderCell: defaultRenderCell,
            valueGetter: (_val, issue) =>
                issue.unitSubPrice && (issue.sharesInUnit ?? 1)
                    ? +issue.unitSubPrice / (issue.sharesInUnit ?? 1)
                    : null,
            valueFormatter: (val, issue) => (val ? formatMoney(val, { currencySymbol: issue.currency }) : '-'),
        },
        {
            field: 'closePrice',
            headerName: t('entities.tableHeaders.closePriceYesterday'),
            renderCell: defaultRenderCell,
            valueGetter: (_val, issue) => {
                return formatMoney(issue.closePrice, {
                    currencySymbol: issue.currency,
                    minDecimals: 2,
                    maxDecimals: 5,
                    locale,
                })
            },
        },
        {
            field: 'marketcap',
            headerName: t('entities.tableHeaders.marketCapYesterday'),
            renderCell: defaultRenderCell,
            valueFormatter: (value, issue) => formatBigMoney(value, { currencySymbol: issue.currency, locale }),
        },
        {
            field: 'legalAdvisors',
            headerName: t('entities.tableHeaders.legalAdvisors'),
            renderCell: defaultRenderCell,
            valueGetter: (_val, issue) => issue.legalAdvisors.map((la) => la.name).join(', '),
        },
        {
            field: 'issuingAgents',
            headerName: t('entities.tableHeaders.issuingAgents'),
            renderCell: defaultRenderCell,
            valueGetter: (_val, issue) => issue.issuingAgents?.map((ia) => ia.name).join(', '),
        },
        {
            field: 'discount10dVwap',
            headerName: t('entities.tableHeaders.discount10dVwap'),
            renderCell: defaultRenderCell,
            valueGetter: (_val, issue) => {
                const { vwap } =
                    issue.tradesStats.find(
                        ({ periodType, numberOfDays }) => periodType === 'prePublication' && numberOfDays === 10,
                    ) ?? {}

                if (!vwap) return null

                return calculateDiscount(issue, vwap, false)
            },
            valueFormatter: (val) => formatPercentage(val, { locale }),
        },
        {
            field: 'terpDiscount10dVwap',
            headerName: t('entities.tableHeaders.terpDiscount10dVwap'),
            renderCell: defaultRenderCell,
            valueGetter: (_val, issue) => {
                const { vwap } =
                    issue.tradesStats.find(
                        ({ periodType, numberOfDays }) => periodType === 'prePublication' && numberOfDays === 10,
                    ) ?? {}

                if (!vwap) return null

                return calculateDiscount(issue, vwap, true)
            },
            valueFormatter: (val) => formatPercentage(val, { locale }),
        },
        {
            field: 'dilutionMax',
            headerName: t('entities.tableHeaders.dilutionMax'),
            renderCell: defaultRenderCell,
            valueGetter: (_val, issue) => calculateDilution(issue).maxDilution,
            valueFormatter: (val) => formatPercentage(val, { locale }),
        },
        {
            field: 'dilutionActual',
            headerName: t('entities.tableHeaders.dilutionActual'),
            renderCell: defaultRenderCell,
            valueGetter: (_val, issue) => calculateDilution(issue).actualDilution,
            valueFormatter: (val) => formatPercentage(val, { locale }),
        },
        {
            field: 'feeLevel1',
            headerName: t('entities.tableHeaders.feeLevel1'),
            renderCell: defaultRenderCell,
            valueFormatter: (value) => {
                if (!value) return '-'

                const desc = getFeeDescription(value, locale)
                if (!desc) return '-'

                return desc
            },
            valueGetter: (_val, issue) => issue.fees.find((f) => f.feeLevel === 1),
        },
        {
            field: 'warrants',
            headerName: t('entities.tableHeaders.warrants'),
            sortComparator: (a, b) => a.length - b.length,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            renderCell: (params: GridRenderCellParams<any, WarrantListItem[]>) => (
                <FullHeightBox sx={{ flexDirection: 'column' }}>
                    {params.value?.map((warrant) => (
                        <div key={warrant.id}>
                            <Link to={`/warrants/${warrant.id}`}>
                                <WarrantName warrant={warrant} />
                            </Link>
                        </div>
                    ))}
                </FullHeightBox>
            ),
        },
        {
            field: 'noncashConsideration',
            headerName: t('entities.tableHeaders.noncashConsideration'),
            renderCell: defaultRenderCell,
        },
        {
            field: 'issueType',
            headerName: t('entities.tableHeaders.issueType'),
            headerAlign: 'left',
            minWidth: 160,
            maxWidth: 400,
            flex: 1,
            align: 'left',
            valueGetter: (_, row) => t(`entities.issueType.values.${row.type}`), // Display the type
            sortComparator: (v1, v2) => v1.localeCompare(v2), // Custom sorting based on the 'type' property
            renderCell: (params) => (
                <Tooltip title='Visa detaljer om emissionen'>
                    <LinkWrapper to={detailsPath(params.row)}>
                        <IssueTypeText issueType={params.row.type} />
                    </LinkWrapper>
                </Tooltip>
            ),
        },
        {
            field: 'shareName',
            headerName: t('entities.tableHeaders.share'),
            headerAlign: 'left',
            minWidth: 180,
            maxWidth: 400,
            flex: 1,
            align: 'left',
            renderCell: (params) => (
                <Tooltip title='Visa detaljer om bolaget'>
                    <LinkWrapper to={`/companies/${params.row.companyId}`}>
                        <Link to={`/companies/${params.row.companyId}`}>
                            {params.row.shareName || params.row.shareId}
                        </Link>
                    </LinkWrapper>
                </Tooltip>
            ),
        },
        {
            field: 'tags',
            headerName: t('entities.tableHeaders.tags'),
            headerAlign: 'left',
            flex: 1,
            minWidth: 90,
            sortable: false,
            display: 'flex',
            align: 'left',
            renderCell: (params) => (
                <LinkWrapper to={detailsPath(params.row)}>
                    <IssueTypeLabels issue={params.row} />
                </LinkWrapper>
            ),
        },
        {
            field: 'status',
            headerName: t('entities.tableHeaders.status'),
            headerAlign: 'left',
            flex: 1,
            align: 'left',
            valueGetter: (_, row) => {
                // lower number will be displayed first in the grid
                if (row.statusComplete === null) return '9' // sort last for now
                const hideUntil =
                    row.statusHideUntil && new Date(row.statusHideUntil) > new Date() ? row.statusHideUntil : null
                if (hideUntil) {
                    return `2-${formatDateYYYYMMDD(hideUntil, { disableNonBreakingDash: true })}` // just before the "all done" status
                }
                if (row.statusComplete) {
                    return row.statusStage && row.statusStage >= 5 ? '5' : '4'
                } else {
                    return '1'
                }
            },
            display: 'flex',
            renderCell: (params) => (
                <LinkWrapper to={detailsPath(params.row)}>
                    <FullHeightBox>
                        <IssueStatusDescription
                            shortForm
                            hideUntil={params.row.statusHideUntil}
                            stage={params.row.statusStage}
                            isComplete={params.row.statusComplete}
                        />
                    </FullHeightBox>
                </LinkWrapper>
            ),
        },
        {
            field: 'date',
            headerName: t('entities.tableHeaders.date'),
            headerAlign: 'left',
            description: t('entities.tableHeaders.dateDescription'),
            align: 'left',
            flex: 1.2,
            minWidth: 120,
            maxWidth: 300,
            width: 250,
            valueGetter: (_, row) => {
                if (row.type === 'IPO') {
                    return isPreviousIssue(row) ? row.ipoTradingDate : row.subEndDate
                }
                return row.type === 'Rights issue' ? row.subEndDate : row.publicationDate
            },
            sortComparator: (v1, v2) => {
                const date1 = new Date(v1).getTime()
                const date2 = new Date(v2).getTime()
                return date1 - date2
            },
            renderCell: (params) => {
                const isIpoOrRights = ['IPO', 'Rights issue'].includes(params.row.type)

                return (
                    <LinkWrapper to={detailsPath(params.row)}>
                        <FullHeightBox>
                            <Typography sx={{ whiteSpace: 'nowrap' }}>
                                {params.row.type === 'Rights issue' && isPreviousIssue(params.row)
                                    ? formatDateYYYYMMDD(params.row.subEndDate, { locale })
                                    : null}
                                {params.row.type === 'IPO' && isPreviousIssue(params.row)
                                    ? formatDateYYYYMMDD(params.row.ipoTradingDate, { locale })
                                    : null}
                                {isIpoOrRights &&
                                    !isPreviousIssue(params.row) &&
                                    `${formatDateYYYYMMDD(params.row.subStartDate, { locale })} - ${formatDateYYYYMMDD(
                                        params.row.subEndDate,
                                        { locale },
                                    )}`}
                                {!isIpoOrRights && formatDateYYYYMMDD(params.row.publicationDate, { locale })}
                            </Typography>
                        </FullHeightBox>
                    </LinkWrapper>
                )
            },
        },
        {
            field: 'sector',
            headerName: t('entities.tableHeaders.issueSector'),
            renderCell: defaultRenderCell,
            valueGetter: (_value, issue) => {
                return translateMillistreamSector(1, issue.sectorLevel1)
            },
        },
        {
            field: 'offeredSize',
            headerName: t('entities.tableHeaders.offeredSize'),
            headerAlign: 'left',
            align: 'left',
            minWidth: 70,
            flex: 1,
            valueGetter: (_value, issue) => {
                return issue.totalUnitSize
            },
            renderCell: (params) => (
                <LinkWrapper to={detailsPath(params.row)}>
                    <FullHeightBox>
                        {params.value != null
                            ? formatBigMoney(params.value, { locale, currencySymbol: params.row.currency })
                            : null}
                    </FullHeightBox>
                </LinkWrapper>
            ),
        },
        {
            field: 'subscribedSize',
            headerName: t('entities.tableHeaders.subscribedSize'),
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            valueGetter: (_value, issue) => {
                return getIssueSubscribedSize(issue)
            },
            renderCell: (params) => (
                <LinkWrapper to={detailsPath(params.row)}>
                    <FullHeightBox>
                        {params.value != null
                            ? formatBigMoney(params.value, { locale, currencySymbol: params.row.currency })
                            : null}
                    </FullHeightBox>
                </LinkWrapper>
            ),
        },
        {
            field: 'financialAdvisors',
            headerName: t('entities.tableHeaders.financialAdvisors'),
            renderCell: defaultRenderCell,
            valueGetter: (_val, issue) => issue.financialAdvisors.map((fa) => fa.name).join(', '),
        },
        {
            field: 'totalCommitment',
            headerName: t('entities.tableHeaders.totalCommitment'),
            headerAlign: 'center',
            align: 'center',
            minWidth: 200,
            flex: 1,
            cellClassName: 'align-content--center',
            renderCell: (params) =>
                ISSUE_TYPES_WITH_COMMIT_CHART.includes(params.row.type) ? (
                    <LinkWrapper to={detailsPath(params.row)}>
                        <FullHeightBox>
                            <CommitChart issue={params.row} size='small' />
                        </FullHeightBox>
                    </LinkWrapper>
                ) : null,
        },
        {
            field: 'outcome',
            headerName: t('entities.tableHeaders.outcome'),
            headerAlign: 'center',
            align: 'center',
            minWidth: 200,
            flex: 1,
            sortable: false,
            cellClassName: 'align-content--center',
            renderCell: (params) =>
                ISSUE_TYPES_WITH_OUTCOME_CHART.includes(params.row.type) ? (
                    <LinkWrapper to={detailsPath(params.row)}>
                        <FullHeightBox>
                            <OutcomeChart issue={params.row} />
                        </FullHeightBox>
                    </LinkWrapper>
                ) : (
                    <LinkWrapper to={detailsPath(params.row)} />
                ),
        },
        {
            field: 'priceChange',
            headerName: t('entities.tableHeaders.priceChange'),
            headerAlign: 'center',
            align: 'center',
            minWidth: 170,
            flex: 1,
            renderHeader: () => (
                <TableSettingHeader
                    header={t('entities.tableHeaders.priceChange')}
                    onClick={onOpenTableSetting}
                    setting='priceChange'
                />
            ),
            sortable: true,
            valueGetter: (_params, issue) => {
                const priceChangeFilterValue = tableSettings.priceChange?.value[0]
                const tradeStats = issue.tradesStats.find(({ periodType }) => periodType === priceChangeFilterValue)

                const priceChange = calculatePriceChange(tradeStats, issue.subscriptionRate)
                return priceChange
            },
            renderCell: ({ formattedValue, ...params }) => {
                if (formattedValue === null) return <LinkWrapper to={detailsPath(params.row)}>-</LinkWrapper>

                return (
                    <Tooltip title='Kursutvecklingen avser förändringen från teckningskursen till dagens aktiekurs, eller aktiekursen vid vald tidsperiod.'>
                        <LinkWrapper to={detailsPath(params.row)}>
                            <FullHeightBox>
                                <div>
                                    <ColoredPercentage value={formattedValue} />
                                </div>
                            </FullHeightBox>
                        </LinkWrapper>
                    </Tooltip>
                )
            },
        },
        {
            field: 'updatedAt',
            headerName: t('entities.tableHeaders.updatedAt'),
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            valueFormatter: (value) => datefns.format(new Date(value), 'yyyy-MM-dd HH:mm:ss'),
        },
    ]

    if (entityIdsWithNotes) {
        const noteIndicator = createNoteIndicatorColumn(entityIdsWithNotes, 'issue', reloadEntityNoteIds)
        cols.push(noteIndicator)
    }

    return cols
}

function CustomNoResultsOverlay() {
    return (
        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body1'>Inga emissioner hittades</Typography>
        </Box>
    )
}

function IssuesGrid({
    availableColumns,
    columnOrder,
    isCompact = false,
    isLoading,
    issues,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onResetColumns,
    sortModel = null,
    pagination = true,
    pageSizeInitialState = 10,
    pageSizeOptions = [10, 25, 50, 100],
    hideFooter = false,
    visibleColumns,
    hasSearchFilter = false,
    detailsPath = null,
}: {
    availableColumns: IssueGridCols[]
    columnOrder?: IssueGridCols[]
    visibleColumns?: Partial<Record<IssueGridCols, boolean>>
    onColumnOrderChange?: (settings: Array<IssueGridCols>) => void
    onColumnVisibilityChange?: (settings: Partial<Record<IssueGridCols, boolean>>) => void
    onResetColumns?: () => void
    isCompact?: boolean
    isLoading?: boolean
    issues: EnrichedIssueEntity[]
    sortModel?: GridSortModel | null
    pagination?: boolean
    pageSizeInitialState?: number
    pageSizeOptions?: number[]
    hideFooter?: boolean
    hasSearchFilter?: boolean
    detailsPath?: ((v: IssueEntity) => string) | null
}) {
    const [popoverAnchorEl, setPopoverAnchorEl] = useState<Element | null>(null)
    const { t } = useTranslation()
    const { language: locale } = useAppLocale()
    const apiRef = useGridApiRef()

    const [issueGridCurrentPage, setIssueGridCurrentPage] = useAtom(issueGridCurrentPageAtom)
    const [searchTextfield, setSearchTextfield] = useAtom<string>(issueSearchTextfieldAtom)
    const [filters] = useAtom(issueFiltersAtom)
    const [tableSettings, setTableSettings] = useAtom(issueTableDataAtom)
    const [tableSettingInEdit, setTableSettingInEdit] = useState<'priceChange' | null>(null)
    const [pageSize, setPageSize] = useState(pageSizeInitialState)
    const { entityIdsWithNotes, reloadEntityNoteIds } = useEntityNoteIds()

    const onOpenTableSetting = useCallback((element: Element, setting: 'priceChange') => {
        setPopoverAnchorEl(element)
        setTableSettingInEdit(setting)
    }, [])

    detailsPath = detailsPath || ((issue) => `/issues/${issue.id}`)

    const columns = useMemo(
        () =>
            issuesCols(
                tableSettings,
                onOpenTableSetting,
                locale,
                t,
                isCompact,
                detailsPath,
                entityIdsWithNotes,
                reloadEntityNoteIds,
            )
                .filter((col) => availableColumns.includes(col.field))
                .sort((a, b) => {
                    if (!columnOrder) return 0

                    return columnOrder.findIndex((v) => a.field === v) - columnOrder.findIndex((v) => b.field === v)
                }),
        [
            availableColumns,
            columnOrder,
            detailsPath,
            entityIdsWithNotes,
            isCompact,
            locale,
            onOpenTableSetting,
            reloadEntityNoteIds,
            t,
            tableSettings,
        ],
    )

    const columnVisibilityModel = React.useMemo(
        () =>
            availableColumns.reduce((acc, col) => {
                return {
                    ...acc,
                    [col]: !visibleColumns ? true : (visibleColumns[col] ?? false),
                }
            }, {}),
        [availableColumns, visibleColumns],
    )

    // Initialize filterModel with quickFilterValues and an empty items array
    const [filterModel, setFilterModel] = useState<GridFilterModel>({
        items: [], // Ensure items is an empty array initially
        quickFilterValues: [hasSearchFilter ? searchTextfield : ''], // Default quick filter value
    })

    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

    useEffect(() => {
        setIssueGridCurrentPage(0)
    }, [setIssueGridCurrentPage, filters])

    const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
        setFilterModel({
            ...newFilterModel,
            items: newFilterModel.items || [], // Ensure items is always an array
        })
        setIssueGridCurrentPage(0)
        setSearchTextfield(newFilterModel.quickFilterValues?.[0])
    }

    const disableSelectedSellOutlineStyle = {
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
        },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
            outline: 'none',
        },
    }

    const handleTableSettingChange = useCallback(
        (selectedItems: Array<(typeof priceChangeSettings)[number]['id']>) => {
            setTableSettings((prevFilters) => ({
                ...prevFilters,
                priceChange: { value: selectedItems },
            }))
        },
        [setTableSettings],
    )

    const handleColumnOrderChange = (
        _params: GridColumnOrderChangeParams,
        _event: unknown,
        details: GridCallbackDetails,
    ) => {
        onColumnOrderChange?.(details.api.getAllColumns().map((c) => c.field) as IssueGridCols[])
    }
    const getRowHeight = React.useCallback(() => (isSmallScreen ? 'auto' : 'auto'), [isSmallScreen])

    const hasColumnCustomization = !isSmallScreen && !!onColumnVisibilityChange

    const priceChangeSettingsTranslations = useMemo(
        () =>
            priceChangeSettings.map((setting) => ({
                ...setting,
                label: t(`entities.tableHeaders.${setting.id}`),
            })),
        [t, locale],
    )
    const grid = (
        // adapt filter: https://mui.com/x/react-data-grid/filtering/customization/#create-a-custom-operator
        <>
            {popoverAnchorEl && tableSettingInEdit === 'priceChange' && (
                <PopoverSelector
                    anchorEl={popoverAnchorEl}
                    label={t('entities.tableHeaders.selectPriceChangePeriodDescription')}
                    onClose={() => setPopoverAnchorEl(null)}
                    onChange={(selectedItems) => void handleTableSettingChange(selectedItems)}
                    options={priceChangeSettingsTranslations}
                    selectedId={tableSettings.priceChange.value}
                />
            )}
            <DataGridPro
                apiRef={apiRef}
                sx={{
                    ...disableSelectedSellOutlineStyle,
                    '& .MuiDataGrid-main': { width: 0, minWidth: '100%' }, // https://github.com/mui/mui-x/issues/8168#issuecomment-1465784733
                    width: '100%',
                    '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
                    '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
                    '& .MuiDataGrid-columnHeader': { cursor: 'move' },
                    '& .even': {
                        backgroundColor: theme.palette.rowColor.main, // Use palette color for even rows
                    },
                    '& .odd': {
                        // backgroundColor: theme.palette.rowColor.light, // Use palette color for odd rows
                    },
                    '& .MuiDataGrid-row': {
                        cursor: 'pointer',
                    },
                    '.align-content--center': {
                        alignContent: 'center',
                    },
                    ...noteIndicatorGridSx,
                }}
                onColumnOrderChange={handleColumnOrderChange}
                sortingOrder={['asc', 'desc']}
                loading={isLoading}
                localeText={getMuiDataGridLocaleText(locale)}
                pagination={pagination}
                columnVisibilityModel={columnVisibilityModel}
                initialState={{
                    sorting: { sortModel: sortModel ?? undefined },
                }}
                pageSizeOptions={pageSizeOptions}
                getRowHeight={getRowHeight}
                disableColumnMenu
                hideFooter={hideFooter}
                disableRowSelectionOnClick
                rows={issues}
                columns={columns}
                density={isCompact ? 'compact' : 'standard'}
                slots={{
                    columnsPanel: CustomColumnsPanel as GridSlots['columnsPanel'],
                    toolbar: CustomDataGridToolbar as GridSlots['toolbar'],
                    noResultsOverlay: CustomNoResultsOverlay,
                    noRowsOverlay: CustomNoResultsOverlay,
                }}
                slotProps={{
                    columnsPanel: {
                        onColumnVisibilityChange,
                        onResetColumns,
                    } as CustomColumnsPanelProps,
                    loadingOverlay: {
                        variant: 'skeleton',
                        noRowsVariant: 'skeleton',
                    },
                    toolbar: {
                        showSearchFilter: hasSearchFilter,
                        showColumnCustomization: !isSmallScreen && !!onColumnVisibilityChange, // Temporary check to only view for admins
                    } as CustomDataGridToolbarProps,
                }}
                showToolbar={hasColumnCustomization || hasSearchFilter}
                getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
                paginationModel={{ pageSize, page: issueGridCurrentPage }}
                onPaginationModelChange={({ page, pageSize: pSize }) => {
                    setIssueGridCurrentPage(page)
                    setPageSize(pSize)
                }}
                filterModel={filterModel}
                onFilterModelChange={handleFilterModelChange} // Capture changes to the filter model
            />
        </>
    )
    return grid
}

export default IssuesGrid
